import { createContext, useContext } from "react";
import type { CollectionItem, CollectionSnapshot, PublicRelease } from "../api/types";

export type CollectionStatus = "idle" | "loading" | "ready" | "error";

export type CollectionContextValue = {
  status: CollectionStatus;
  revision: number | null;
  itemsByReleaseId: ReadonlyMap<string, CollectionItem>;
  items: CollectionItem[];
  uniqueCount: number;
  totalQuantity: number;
  errorMessage: string | null;
  conflictMessage: string | null;
  mutatingReleaseId: string | null;
  /** True when showing last-known offline snapshot (not authoritative). */
  stale: boolean;
  online: boolean;
  mutationsDisabled: boolean;
  clearConflictMessage: () => void;
  refresh: () => Promise<void>;
  getQuantity: (releaseId: string) => number;
  setQuantity: (releaseId: string, quantity: number) => Promise<void>;
  undoActivity: (activityId: string) => Promise<void>;
  undoToast: { activityId: string; message: string } | null;
  dismissUndoToast: () => void;
  importReplace: (
    items: Array<{ releaseId: string; quantity: number }>,
  ) => Promise<CollectionSnapshot>;
  /** Fresh GET /collection then return snapshot for export. Online only. */
  exportFresh: () => Promise<CollectionSnapshot>;
  patchRelease: (release: PublicRelease) => void;
};

export const CollectionContext = createContext<CollectionContextValue | null>(
  null,
);

export function useCollection(): CollectionContextValue {
  const value = useContext(CollectionContext);
  if (!value) {
    throw new Error("useCollection must be used within CollectionProvider");
  }
  return value;
}
